import type { ReplayReactionEvent } from '../../shared/replayReactionEvent';

// Decides which reaction events are due at a given playhead position.
//
// The events arrive already lag-corrected and sorted by t_ms (spec §6.3), so this
// only has to walk a cursor forward — and handle the two things a real player
// does that a naive cursor gets wrong: seeking backwards, and stalling for
// several seconds while buffering.
export function createReplayStreamScheduler(
  events: ReplayReactionEvent[],
  options: { maxPerFrame: number }
) {
  let cursor = 0;
  let lastTimeMs = -1;

  function seekCursorTo(timeMs: number): void {
    // Binary search rather than a rescan: a creator dragging the scrubber
    // generates a seek per frame, and this runs inside requestAnimationFrame.
    let low = 0;
    let high = events.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      if ((events[mid]?.t_ms ?? 0) < timeMs) low = mid + 1;
      else high = mid;
    }
    cursor = low;
  }

  return {
    reset(timeMs: number): void {
      seekCursorTo(timeMs);
      lastTimeMs = timeMs;
    },

    due(timeMs: number): ReplayReactionEvent[] {
      // Backwards seek, or first frame. Jump the cursor and emit nothing this
      // frame — replaying a scrub backwards as a burst would be noise.
      if (timeMs < lastTimeMs || lastTimeMs < 0) {
        seekCursorTo(timeMs);
        lastTimeMs = timeMs;
        return [];
      }

      const ready: ReplayReactionEvent[] = [];
      let skipped = 0;

      while (cursor < events.length) {
        const event = events[cursor];
        if (!event || event.t_ms > timeMs) break;
        cursor += 1;
        // Spec §6.3: cap spawns per frame. A genuine spike can be forty
        // simultaneous reactions and uncapped it renders as an unreadable wall —
        // which is exactly the moment the creator most wants to read. Events past
        // the cap are dropped rather than deferred: showing them a frame late
        // would misplace them in time, and replay conveys feel, not magnitude.
        if (ready.length < options.maxPerFrame) ready.push(event);
        else skipped += 1;
      }

      lastTimeMs = timeMs;
      void skipped;
      return ready;
    },

    total(): number {
      return events.length;
    }
  };
}
